/**
 * "You have not selected a site" — recognised, and said plainly.
 *
 * Dashboard reads are site-scoped. When a SITE-SCOPED user (one whose role is
 * not corporate — see backend utils/site_context.CORPORATE_ROLES) has no site
 * selected, the server fails closed rather than dropping the filter and
 * showing them every site in the tenant:
 *
 *   400 {"detail": "site_id required: please select a site to view this dashboard"}
 *
 * That is the right answer, but each widget rendered it in its own way: the
 * trend chart said "Failed to load chart" while the KPI cards printed "no
 * data" and the backlog strip showed dashes. The page looked broken, and
 * nothing anywhere told the reader to pick a site.
 */
import React from 'react';
import { MapPin } from 'lucide-react';

/**
 * True when this error is the server asking for a site selection, rather than
 * a genuine failure. Matched on status + the detail the endpoint sends, so an
 * unrelated 400 still reports as an error.
 */
export function isSiteRequiredError(error) {
  const res = error?.response;
  if (!res || res.status !== 400) return false;
  const detail = res.data?.detail;
  return typeof detail === 'string' && detail.toLowerCase().startsWith('site_id required');
}

/**
 * The replacement for a widget's error state in that case. `compact` suits a
 * KPI strip or a small card; the default fills a chart's box.
 */
export function SiteRequiredNotice({ compact = false, className = '' }) {
  if (compact) {
    return (
      <div className={`flex items-center gap-2 rounded-lg border border-dashed border-gray-300
                       bg-gray-50 px-3.5 py-3 text-sm text-gray-600 ${className}`}>
        <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
        <span>Select a site to view this dashboard.</span>
      </div>
    );
  }
  return (
    <div className={`flex h-full min-h-[140px] flex-col items-center justify-center gap-2
                     px-6 py-8 text-center ${className}`}>
      <MapPin className="h-6 w-6 text-gray-300" />
      <p className="text-sm font-medium text-gray-700">Select a site to view this dashboard</p>
      <p className="max-w-xs text-xs text-gray-500">
        Your access is scoped to specific sites, so this data has to be read one site at a
        time. Pick one from the site selector at the top of the page.
      </p>
    </div>
  );
}

export default SiteRequiredNotice;
